import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ClientHeader } from '../components/client/ClientHeader';
import BookingReminderBanner from '../components/BookingReminderBanner';
import BookingDocuments from '../components/BookingDocuments';
import { LoadingCard, STATUS_COLOR, money, fmtDateTime, fmtDate } from '../components/client/clientShared';
import {
  Clock, MapPin, PawPrint, User, Phone, Mail, Receipt, FileText, Camera,
  CalendarClock, RefreshCw, XCircle, RotateCcw, Plus, Loader2, ExternalLink, Star,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientBookingDetailPage() {
  const { bookingId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [b, setB] = useState(null);
  const [noteDialog, setNoteDialog] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [reschedDialog, setReschedDialog] = useState(false);
  const [resched, setResched] = useState({ requestedDate: '', requestedTime: '', reason: '' });
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [photoUrls, setPhotoUrls] = useState({});

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client/bookings/${bookingId}`, { headers });
      setB(res.data);
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Booking not found' : 'Could not load booking');
      navigate('/client/bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);

  // Fetch photos as authenticated blobs
  useEffect(() => {
    if (!b?.clientPhotos?.length) return;
    let revoked = [];
    (async () => {
      for (const ph of b.clientPhotos) {
        if (photoUrls[ph.id]) continue;
        try {
          const res = await axios.get(`${API_URL}/api/client/bookings/${bookingId}/photos/${ph.id}`, { headers, responseType: 'blob' });
          const url = URL.createObjectURL(res.data);
          revoked.push(url);
          setPhotoUrls((prev) => ({ ...prev, [ph.id]: url }));
        } catch { /* ignore */ }
      }
    })();
    return () => revoked.forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b?.clientPhotos]);

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/client/bookings/${bookingId}/notes`, { text: noteText.trim() }, { headers });
      toast.success('Note added');
      setNoteText(''); setNoteDialog(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not add note'); }
    finally { setBusy(false); }
  };

  const submitReschedule = async () => {
    if (!resched.requestedDate) { toast.error('Please choose a date'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/client/bookings/${bookingId}/reschedule-request`, resched, { headers });
      toast.success('Reschedule request sent to your provider');
      setReschedDialog(false); setResched({ requestedDate: '', requestedTime: '', reason: '' }); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send request'); }
    finally { setBusy(false); }
  };

  const submitCancel = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/client/bookings/${bookingId}/cancel`, { reason: cancelReason.trim() || undefined }, { headers });
      toast.success('Booking cancelled');
      setCancelDialog(false); setCancelReason(''); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not cancel'); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/client/bookings/${bookingId}/photos`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo added');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not upload photo'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (loading) {
    return <div className="min-h-screen bg-background"><ClientHeader backTo="/client/bookings" title="Booking" /><div className="max-w-3xl mx-auto p-6"><LoadingCard /></div></div>;
  }
  if (!b) return null;

  const provider = b.provider || {};
  const pay = b.invoice?.status || b.paymentStatus;
  const rr = b.rescheduleRequest;

  return (
    <div className="min-h-screen bg-background">
      <ClientHeader backTo="/client/bookings" title="Booking Details" subtitle={b.serviceName} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">

        <BookingReminderBanner bookings={[b]} />

        {rr?.status === 'PENDING' && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="reschedule-pending-banner">
            <strong>Reschedule requested:</strong> you asked to move this to {rr.requestedDate}{rr.requestedTime ? ` at ${rr.requestedTime}` : ''}. Waiting for provider confirmation.
          </div>
        )}

        {/* Overview */}
        <Card data-testid="booking-overview">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge className={STATUS_COLOR[b.status] || 'bg-gray-100 text-gray-700'} data-testid="booking-status">{b.status}</Badge>
              {pay && <Badge variant="outline" className={STATUS_COLOR[pay] || ''} data-testid="booking-payment-status">{pay}</Badge>}
            </div>
            <h2 className="text-xl font-bold">{b.serviceName || 'Service'}</h2>
            <div className="mt-3 space-y-2 text-sm">
              <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" /> {fmtDateTime(b.startDateTime)}{b.endDateTime ? ` – ${fmtDateTime(b.endDateTime).split(', ').pop()}` : ''}</p>
              {(b.locationAddress || b.jobAddress || b.jobLocation) && (
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> {b.locationAddress || b.jobAddress || b.jobLocation}</p>
              )}
              {b.riderCount != null && <p className="flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground" /> Riders: <span className="font-semibold" data-testid="booking-rider-count">{b.riderCount}</span></p>}
              {b.totalCents != null && <p className="flex items-center gap-2"><Receipt className="h-4 w-4 text-muted-foreground" /> Price: <span className="font-semibold">{money(b.totalCents)}</span></p>}
            </div>
          </CardContent>
        </Card>

        {/* Provider */}
        <Card data-testid="booking-provider">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><User className="h-4 w-4" /> Provider</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="font-semibold">{provider.name || '—'}</p>
            {(provider.city || provider.state) && <p className="text-muted-foreground">{[provider.city, provider.state].filter(Boolean).join(', ')}</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              {provider.phone && <Button size="sm" variant="outline" asChild data-testid="contact-phone-btn"><a href={`tel:${provider.phone}`}><Phone className="h-3 w-3 mr-1" /> Call</a></Button>}
              {provider.email && <Button size="sm" variant="outline" asChild data-testid="contact-email-btn"><a href={`mailto:${provider.email}`}><Mail className="h-3 w-3 mr-1" /> Email</a></Button>}
              {provider.slug && <Button size="sm" variant="outline" onClick={() => navigate(`/providers/${provider.slug}`)} data-testid="view-provider-btn"><ExternalLink className="h-3 w-3 mr-1" /> View Provider</Button>}
            </div>
          </CardContent>
        </Card>

        {/* Animals */}
        {b.animals?.length > 0 && (
          <Card data-testid="booking-animals">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><PawPrint className="h-4 w-4" /> Animals</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {b.animals.map((a) => <Badge key={a.id} variant="secondary" data-testid={`booking-animal-${a.id}`}>{a.name}{a.breed ? ` · ${a.breed}` : ''}</Badge>)}
            </CardContent>
          </Card>
        )}

        {/* Invoice */}
        {b.invoice && (
          <Card data-testid="booking-invoice">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Receipt className="h-4 w-4" /> Invoice</CardTitle></CardHeader>
            <CardContent className="flex items-center justify-between text-sm">
              <div>
                <p className="font-medium">{b.invoice.invoiceNumber}</p>
                <p className="text-muted-foreground">{money(b.invoice.totalCents)} · Balance {money(b.invoice.balanceCents)}</p>
              </div>
              {b.invoice.publicToken && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/invoice/${b.invoice.publicToken}`)} data-testid="view-invoice-btn">
                  <ExternalLink className="h-3 w-3 mr-1" /> View
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Booking notes (creation) + client notes */}
        <Card data-testid="booking-notes">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Notes</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setNoteDialog(true)} data-testid="add-note-btn"><Plus className="h-3 w-3 mr-1" /> Add Note</Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {b.notes && <div className="rounded-md bg-muted p-2"><span className="text-xs text-muted-foreground">Booking note:</span> {b.notes}</div>}
            {b.locationNotes && <div className="rounded-md bg-muted p-2"><span className="text-xs text-muted-foreground">Location note:</span> {b.locationNotes}</div>}
            {(b.clientNotes || []).length === 0 && !b.notes && !b.locationNotes && <p className="text-muted-foreground">No notes yet.</p>}
            {(b.clientNotes || []).map((n) => (
              <div key={n.id} className="rounded-md border p-2" data-testid={`client-note-${n.id}`}>
                <p>{n.text}</p>
                <p className="text-xs text-muted-foreground mt-1">{fmtDateTime(n.createdAt)}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Photos */}
        <Card data-testid="booking-photos">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><Camera className="h-4 w-4" /> Photos</CardTitle>
            <>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="add-photo-btn">
                {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />} Add Photo
              </Button>
            </>
          </CardHeader>
          <CardContent>
            {(b.clientPhotos || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No photos yet.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {b.clientPhotos.map((ph) => (
                  <div key={ph.id} className="aspect-square rounded-md border overflow-hidden bg-muted flex items-center justify-center" data-testid={`booking-photo-${ph.id}`}>
                    {photoUrls[ph.id]
                      ? <img src={photoUrls[ph.id]} alt="Booking" className="w-full h-full object-cover" />
                      : <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents (RW-BUILD-014) */}
        <BookingDocuments bookingId={bookingId} role="client" />

        {/* Cancellation policy */}
        {provider.cancellationPolicyText && (
          <p className="text-xs text-muted-foreground px-1" data-testid="cancellation-policy">Cancellation policy: {provider.cancellationPolicyText}</p>
        )}

        {/* Actions */}
        <Card data-testid="booking-actions">
          <CardHeader className="pb-2"><CardTitle className="text-base">Actions</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {b.canRequestReschedule ? (
              <Button variant="outline" onClick={() => setReschedDialog(true)} data-testid="request-reschedule-btn"><RefreshCw className="h-4 w-4 mr-1" /> Request Reschedule</Button>
            ) : b.rescheduleReason ? (
              <Button variant="outline" disabled title={b.rescheduleReason}><RefreshCw className="h-4 w-4 mr-1" /> Request Reschedule</Button>
            ) : null}
            {b.canCancel ? (
              <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={() => setCancelDialog(true)} data-testid="cancel-booking-btn"><XCircle className="h-4 w-4 mr-1" /> Cancel Booking</Button>
            ) : b.cancelReason && b.status !== 'CANCELED' ? (
              <Button variant="outline" disabled title={b.cancelReason}><XCircle className="h-4 w-4 mr-1" /> Cancel Booking</Button>
            ) : null}
            {b.canReview && (
              <Button
                variant="outline"
                onClick={() => navigate(`/review/${b.id}?providerId=${provider.id || ''}&providerName=${encodeURIComponent(provider.name || '')}&providerSlug=${provider.slug || ''}`)}
                data-testid="leave-review-btn"
              >
                <Star className="h-4 w-4 mr-1" /> Leave a Review
              </Button>
            )}
            {b.canRebook && (
              <Button variant="outline" onClick={() => navigate(`/providers/${provider.slug}`)} data-testid="rebook-btn"><RotateCcw className="h-4 w-4 mr-1" /> Rebook</Button>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Note Dialog */}
      <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
        <DialogContent data-testid="note-dialog">
          <DialogHeader><DialogTitle>Add a note</DialogTitle><DialogDescription>Your provider will be able to see this.</DialogDescription></DialogHeader>
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="e.g. Gate code is 4321, the horse is in the back paddock…" rows={4} data-testid="note-textarea" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(false)}>Cancel</Button>
            <Button onClick={submitNote} disabled={busy || !noteText.trim()} data-testid="submit-note-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Note'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={reschedDialog} onOpenChange={setReschedDialog}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle>Request a reschedule</DialogTitle><DialogDescription>Propose a new time. Your provider will confirm the change.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label htmlFor="rd">Preferred date</Label><Input id="rd" type="date" value={resched.requestedDate} onChange={(e) => setResched((r) => ({ ...r, requestedDate: e.target.value }))} data-testid="reschedule-date" /></div>
            <div><Label htmlFor="rt">Preferred time (optional)</Label><Input id="rt" type="time" value={resched.requestedTime} onChange={(e) => setResched((r) => ({ ...r, requestedTime: e.target.value }))} data-testid="reschedule-time" /></div>
            <div><Label htmlFor="rrs">Reason (optional)</Label><Textarea id="rrs" value={resched.reason} onChange={(e) => setResched((r) => ({ ...r, reason: e.target.value }))} rows={2} data-testid="reschedule-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedDialog(false)}>Cancel</Button>
            <Button onClick={submitReschedule} disabled={busy || !resched.requestedDate} data-testid="submit-reschedule-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Request'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <AlertDialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <AlertDialogContent data-testid="cancel-dialog">
          <AlertDialogHeader><AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>This will notify your provider. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <div><Label htmlFor="cr">Reason (optional)</Label><Textarea id="cr" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2} data-testid="cancel-reason" /></div>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); submitCancel(); }} className="bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cancel Booking'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
